import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Stack, IconButton, Tooltip } from '@mui/material';
import { Add, Edit, Delete, OpenInNew } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { useStyles } from '../styles/projectLinksStyles';
import AddProjectItemModal from './popups/AddProjectItemModal';
import { DeleteModal } from './popups';
import {
  getProjectLinks,
  createProjectLink,
  updateProjectLink,
  deleteProjectLink,
} from '../../../../services/projectLinksService';
import { isAdmin, isStakeholder } from '../../../../utils/share';

const ProjectLinks = ({ projectId }) => {
  const classes = useStyles();
  const [links, setLinks] = useState([]);
  const [modalOpen, setModalOpen] = useState({ action: '' });
  const [selectedLink, setSelectedLink] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const accountData = useSelector((state) => state?.login?.user || null);

  const fetchLinks = useCallback(async () => {
    try {
      const response = await getProjectLinks(projectId);
      // Filter only overview items (type: 'overview') for overview section
      const allItems = response?.data?.data || [];
      const linkItems = allItems.filter((item) => item.type === 'overview');

      // Temporary: if no overview items, show all items for debugging
      if (linkItems.length === 0 && allItems.length > 0) {
        setLinks([]);
      } else {
        setLinks(linkItems);
      }
    } catch (error) {
      toast.error('Failed to fetch project links');
      console.error(error);
    }
  }, [projectId]);

  useEffect(() => {
    if (projectId) {
      fetchLinks();
    }
  }, [projectId]); // Remove fetchLinks from dependencies to prevent infinite loop

  // Handle modal open/close
  const handleAddModalOpen = useCallback(() => {
    setModalOpen({ action: 'add' });
  }, []);

  const handleAddModalClose = useCallback(() => {
    setModalOpen({ action: '' });
  }, []);

  const handleEditModalOpen = useCallback((link) => {
    setSelectedLink(link);
    setModalOpen({ action: 'edit' });
  }, []);

  const handleEditModalClose = useCallback(() => {
    setModalOpen({ action: '' });
    setSelectedLink(null);
  }, []);

  // Handle adding new item (link or file)
  const handleAddItem = async (itemData) => {
    try {
      setIsCreating(true);
      const response = await createProjectLink({
        ...itemData,
        projectId,
        createdBy: accountData?.id,
        type: 'overview', // Always set as 'overview' for overview section
      });
      toast.success(response?.data?.message || 'Item added successfully');
      await fetchLinks(); // Refetch to get updated data
      handleAddModalClose();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to add link');
    } finally {
      setIsCreating(false);
    }
  };

  // Handle updating link
  const handleUpdateLink = async (linkData) => {
    try {
      setIsCreating(true);
      const response = await updateProjectLink(selectedLink.id, {
        ...linkData,
        type: 'overview', // Keep as 'overview' for overview section
      });
      toast.success(response?.data?.message || 'Item updated successfully');
      await fetchLinks(); // Refetch to get updated data
      handleEditModalClose();
    } catch (error) {
      console.error(error);
      toast.error(error?.response?.data?.message || 'Failed to update link');
    } finally {
      setIsCreating(false);
    }
  };

  // Handle delete confirmation
  const handleConfirmDelete = async () => {
    if (selectedLink) {
      try {
        setIsDeleting(true);
        const response = await deleteProjectLink(selectedLink.id);
        toast.success(response?.data?.message || 'Link deleted successfully');

        // Update local state instead of refetching to avoid infinite loops
        setLinks((prevLinks) => prevLinks.filter((link) => link.id !== selectedLink.id));
        setSelectedLink(null);
        setModalOpen({ action: '' });
      } catch (error) {
        console.error('Delete error:', error);
        toast.error(error?.response?.data?.message || 'Failed to delete link');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleDeleteClick = useCallback((link) => {
    setSelectedLink(link);
    setModalOpen({ action: 'delete' });
  }, []);

  const handleLinkClick = useCallback((url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const AddButton = () => (
    <Box className={classes.linkButton} onClick={!isAdmin() ? null : handleAddModalOpen}>
      <Box className={classes.addIcon}>
        <Add />
      </Box>
      <Typography variant="body2" className={classes.buttonText}>
        Add Project Link
      </Typography>
    </Box>
  );
  return (
    <Stack spacing={2}>
      <Typography variant="h4" color="GrayText">
        Project Links
      </Typography>

      <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
        <AddButton />
        {links.map((link) => (
          <Box key={link.id} display="flex" alignItems="center" className={classes.linkButton}>
            <Box
              display="flex"
              alignItems="center"
              sx={{ flexGrow: 1, marginRight: 1, cursor: 'pointer' }}
              onClick={() => handleLinkClick(link.linkUrl)}
            >
              <Tooltip title={link.linkTitle} placement="top" arrow>
                <Typography variant="body2" className={classes.linkName}>
                  {link.linkTitle}
                </Typography>
              </Tooltip>
              <OpenInNew fontSize="small" sx={{ ml: 1, color: 'primary.main' }} />
            </Box>

            <Box display="flex" alignItems="center">
              {!isStakeholder() && (
                <>
                  <IconButton
                    disabled={!isAdmin()}
                    size="small"
                    onClick={() => handleEditModalOpen(link)}
                    className={classes.editButton}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    disabled={!isAdmin()}
                    size="small"
                    onClick={() => handleDeleteClick(link)}
                    className={classes.deleteButton}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </>
              )}
            </Box>
          </Box>
        ))}
      </Stack>

      {/* Add Item Modal */}
      <AddProjectItemModal
        open={modalOpen.action === 'add' || modalOpen.action === 'edit'}
        onClose={() => {
          setModalOpen({ action: '' });
          setSelectedLink(null);
        }}
        link={selectedLink}
        onSubmit={modalOpen.action === 'add' ? handleAddItem : handleUpdateLink}
        context="overview"
        isLoading={isCreating}
      />

      {/* Delete Confirmation Modal */}
      <DeleteModal
        open={modalOpen.action === 'delete'}
        onClose={() => setModalOpen({ action: '' })}
        onDelete={handleConfirmDelete}
        isLoading={isDeleting}
      />
    </Stack>
  );
};

ProjectLinks.propTypes = {
  projectId: PropTypes.string.isRequired,
};

export default ProjectLinks;
